import { VmStatus } from "../cloud-subscription/enums/vm-status.enum";
import { Region, ServerProfiles } from "../interfaces/plan.interface";
import { AWSVpcPeeringStatus, AzureVnetPeeringStatus, VmStatusType } from "../types/vm.type";
import { BillingTerm } from "../types/subscription.type";
import { BillingTermEnum } from "../enums/subscription.enum";
import { CloudVendorType } from "../paid-subscription/types/cloud-vendor.type";
import { CloudVendor } from "../enums/cloud-vendor.enum";
import { AWSVpcPeeringStatuses, AzureVnetPeeringStatuses, ConnectionTypeEnum } from "../enums/vm.enum";
import { ConnectionType } from "../types/vm.type";
import { NullableString } from "../types/az-add-trusted-tenant.type";
import { Keys } from "./keys";

export default class StringHelper {

    static replacePlaceholders(template: string, values: Record<string, any>): string {
        return Object.keys(values).reduce((result, key) => {
            const placeholder = new RegExp(`:${key}`, 'g');
            return result.replace(placeholder, encodeURIComponent(String(values[key])))
        }, template);
    }

    static formatUrlWithParams(url: string, ...args: (string | number)[]): string {
        return url.replace(/{(\d+)}/g, (match, number) => {
            const value = args[number];
            return (number < args.length && value !== undefined) ? encodeURIComponent(String(value)) : match;
        });
    }

    static capitalizeFirstLetter(input: string | null): string {
        if (!input) return '';
        return input.charAt(0).toUpperCase() + input.slice(1);
    }

    static getStatusClass(status: VmStatusType): string {
        switch (status) {
            case VmStatus.Starting:
                return 'btn-warning';
            case VmStatus.Running:
                return 'btn-primary';
            case VmStatus.Stopping:
                return 'btn-warning';
            case VmStatus.Stopped:
                return 'btn-danger';
            case VmStatus.Deallocating:
                return 'btn-warning';
            case VmStatus.Deallocated:
                return 'btn-secondary';
            case VmStatus.Unknown:
                return 'btn-dark';
            default:
                return 'btn-dark';
        }
    }

    static isValidValue(value: any): boolean {
        
        if (typeof value === 'string') {
            return value !== null && value !== undefined && value.trim().length > 0;
        }
        
        return value !== null && value !== undefined;
    }

    static sortServerProfiles(serverProfiles: ServerProfiles[]): ServerProfiles[] {

        return serverProfiles.sort((a, b) => {

            const aRam = this.extractRam(a.displayName)
            const bRam = this.extractRam(b.displayName)

            if (aRam !== bRam) {
                return aRam - bRam
            }

            const aVcpu = this.extractVcpu(a.displayName)
            const bVcpu = this.extractVcpu(b.displayName)
            return aVcpu - bVcpu

        })

    }

    static extractRam(displayName: string): number {
        const parts = displayName.split('-')[1]?.split('/') || []
        return parts.length > 0 ? parseInt(parts[0], 10) : Infinity
    }

    static extractVcpu(displayName: string): number {
        const parts = displayName.split('-')[1]?.split('/') || []
        return parts.length > 1 ? parseInt(parts[1], 10) : Infinity
    }

    static sortRegions(regions: Region[]): Region[] {
        return regions.sort((a, b) => a.displayName.localeCompare(b.displayName))
    }

    static isPAYG(billingTerm: BillingTerm): boolean {
        return billingTerm === BillingTermEnum.Monthly
    }

    static isAnnual(billingTerm: BillingTerm): boolean {
        return billingTerm === BillingTermEnum.OneYear || billingTerm === BillingTermEnum.ThreeYear
    }

    static isAWS(cloudVendor: CloudVendorType | Lowercase<CloudVendorType>): boolean {
        return CloudVendor.AWS.toLowerCase() === cloudVendor.toLowerCase()
    }

    static isAzure(cloudVendor: CloudVendorType | Lowercase<CloudVendorType>): boolean {
        return CloudVendor.Azure.toLowerCase() === cloudVendor.toLowerCase()
    }

    static isPeering(peeringType: ConnectionType): boolean {
        return peeringType.toLowerCase() === ConnectionTypeEnum.PEERING.toLowerCase()
    }
    
    static isPrivateLink(peeringType: ConnectionType): boolean {
        return peeringType.toLowerCase() === ConnectionTypeEnum.PRIVATE_LINK.toLowerCase()
    }

    static getLastSegment(id: string): string {
        if (!id) return ''
        const segments = id.split('/')
        return segments.pop() || ''
    }

    static formatPeeringStatus = (status: AzureVnetPeeringStatus | AWSVpcPeeringStatus): string => {
        return status
            .toLowerCase() 
            .split('_') 
            .map(word => word.charAt(0).toUpperCase() + word.slice(1)) 
            .join(' ')
    }
    
    static validateRequest(request: { [key: string]: any }): string[] {

        const missingProperties: string[] = []

        for (const [property, value] of Object.entries(request)) {

            if (!StringHelper.isValidValue(value)) {
                missingProperties.push(property)
            }

        }

        return missingProperties

    }

    static getPeeringStatus(vendor: CloudVendorType, status: AWSVpcPeeringStatus | AzureVnetPeeringStatus): string {

        const awsClassMapping: Record<AWSVpcPeeringStatus, string> = {
            [AWSVpcPeeringStatuses.INIT]: "init",
            [AWSVpcPeeringStatuses.INVITE_SENT]: "guest-invite-sent",
            [AWSVpcPeeringStatuses.ROLE_ASSIGNED]: "network-contributor-role-assigned",
            [AWSVpcPeeringStatuses.PENDING]: "pending",
            [AWSVpcPeeringStatuses.ACTIVE]: "active",
            [AWSVpcPeeringStatuses.PENDING_ACCEPTANCE]: "pending-acceptance",
            [AWSVpcPeeringStatuses.DELETING]: "deleting",
            [AWSVpcPeeringStatuses.DELETED]: "deleted",
            [AWSVpcPeeringStatuses.EXPIRED]: "expired",
            [AWSVpcPeeringStatuses.REJECTED]: "rejected",
            [AWSVpcPeeringStatuses.FAILED]: "failed",
            [AWSVpcPeeringStatuses.DELETION_FAILED]: "deletion-failed",
            [AWSVpcPeeringStatuses.AWAITING_ROUTE_ENTRY]: "awaiting-route-entry",
            [AWSVpcPeeringStatuses.AWAITING_UPDATE_RULES]: "awaiting-update-rules",
            [AWSVpcPeeringStatuses.UNKNOWN]: "unknown"
        }

        const azureClassMapping: Record<AzureVnetPeeringStatus, string> = {
            [AzureVnetPeeringStatuses.INIT]: "init",
            [AzureVnetPeeringStatuses.EXCHANGE_GUEST_ACCESS]: "exchange_guest_access",
            [AzureVnetPeeringStatuses.INVITE_SENT]: "guest-invite-sent",
            [AzureVnetPeeringStatuses.ROLE_ASSIGNED]: "network-contributor-role-assigned",
            [AzureVnetPeeringStatuses.INITIATED]: "initiated",
            [AzureVnetPeeringStatuses.CONNECTED]: "connected",
            [AzureVnetPeeringStatuses.DISCONNECTED]: "disconnected",
            [AzureVnetPeeringStatuses.UPDATING]: "updating",
            [AzureVnetPeeringStatuses.DELETING]: "deleting",
            [AzureVnetPeeringStatuses.FAILED]: "failed",
            [AzureVnetPeeringStatuses.PENDING]: "pending",
            [AzureVnetPeeringStatuses.UNKNOWN]: "unknown"
        }

        if (vendor.toLowerCase() === CloudVendor.AWS.toLowerCase()) {
            return awsClassMapping[status as AWSVpcPeeringStatus] || AWSVpcPeeringStatuses.UNKNOWN

        } else if (vendor.toLowerCase() === CloudVendor.Azure.toLowerCase()) {
            return azureClassMapping[status as AzureVnetPeeringStatus] || AzureVnetPeeringStatuses.UNKNOWN
        }
    }

    static removeWhitespace(input: NullableString): NullableString {

        if (typeof input === 'string') {
            return input.trim(); 
        }

        return input; 
    }

    static getAzVirtualNetworkName(resourceId: string, cloudVendor: CloudVendorType): string {

        if (this.isAWS(cloudVendor)) {
          return '';
        }

        if (!resourceId || typeof resourceId !== 'string') {
          return '';
        }
    
        const parts = resourceId.split('/');
    
        const isAzureVNetResource = parts.length >= 9 && parts[7] === Keys.AZURE_VIRTUAL_NETWORKS;
    
        if (isAzureVNetResource) {
          return parts[8];
        }
    
        return '';
    };

    static getVendorName(cloudVendor: CloudVendorType): string {
        if (this.isAWS(cloudVendor)) {
            return 'AWS';
        }

        if (this.isAzure(cloudVendor)) {
            return 'Azure';
        }

        return '';
    }

    static getAzResourceGroupName(resourceId: string, cloudVendor: CloudVendorType): string {

        if (this.isAWS(cloudVendor)) {
            return '';
        }

        if (!resourceId || typeof resourceId !== 'string') {
            return '';
        }

        const parts = resourceId.split('/');

        const isAzureResource = parts.length >= 5 && parts[3] === Keys.RESOURCE_GROUPS;

        if (isAzureResource) {
            return parts[4];
        }

        return '';
    }

    static replacePublicIpHelper(publicIp : string, linkTemplate:string):string{
        return linkTemplate.replace('%public_ip%',publicIp)
    }
    

}